import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Slider, Dialog, DialogContent, DialogActions,
  IconButton, InputAdornment, Chip, Autocomplete, CircularProgress,
} from '@mui/material';
import {
  SearchOutlined, MyLocationOutlined, GpsFixedOutlined, CloseOutlined,
} from '@mui/icons-material';
import { GoogleMap, CircleF, MarkerF } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import {
  GoogleMapsErrorBoundary,
  GoogleMapsUnavailableDialog,
  useSvdyGoogleMaps,
} from '../../../components/location/googleMaps';

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

export interface GeofenceFormData {
  address: string;
  radius: number;
  lat: number;
  lng: number;
}

interface GeofenceMapDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: GeofenceFormData) => void;
  title: string;
  subtitle?: string;
  initialData?: GeofenceFormData;
  extraFields?: React.ReactNode;
  saveDisabled?: boolean;
  saveLabel?: string;
}

const GeofenceMapDialogLoaded: React.FC<GeofenceMapDialogProps> = ({
  open, onClose, onSave, title, subtitle, initialData, extraFields, saveDisabled, saveLabel,
}) => {
  const { t } = useTranslation();
  const finalSaveLabel = saveLabel ?? t('common:actions.save');
  const { isLoaded, loadError } = useSvdyGoogleMaps();
  const mapsAvailable = isLoaded && !loadError;

  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState(200);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const mapRef = useRef<google.maps.Map | null>(null);

  // Places search driven manually (not @react-google-maps/api's <Autocomplete>
  // wrapper — that wrapper mutates the MUI <input> and silently shows no
  // predictions inside a Dialog; see LocationPickerDialog docstring). We use
  // AutocompleteService + PlacesService into a MUI Autocomplete instead.
  interface Prediction { place_id: string; description: string; }
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const placesHostRef = useRef<HTMLDivElement | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState<Prediction[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    if (mapsAvailable && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
  }, [mapsAvailable]);

  const ensurePlacesService = useCallback(() => {
    if (placesService.current) return placesService.current;
    if (!mapsAvailable || typeof google === 'undefined') return null;
    const host = placesHostRef.current ?? (() => {
      const d = document.createElement('div');
      d.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
      document.body.appendChild(d);
      return d;
    })();
    placesService.current = new google.maps.places.PlacesService(host);
    return placesService.current;
  }, [mapsAvailable]);

  // Debounced predictions as the user types.
  useEffect(() => {
    if (!mapsAvailable || !autocompleteService.current) return;
    const q = searchInput.trim();
    if (q.length < 2) { setOptions([]); return; }
    setOptionsLoading(true);
    const id = setTimeout(() => {
      autocompleteService.current?.getPlacePredictions({ input: q }, (preds, status) => {
        setOptionsLoading(false);
        if (status !== google.maps.places.PlacesServiceStatus.OK || !preds) { setOptions([]); return; }
        setOptions(preds.map((p) => ({ place_id: p.place_id, description: p.description })));
      });
    }, 250);
    return () => clearTimeout(id);
  }, [searchInput, mapsAvailable]);

  const resolvePlace = useCallback((placeId: string) => {
    const svc = ensurePlacesService();
    if (!svc) return;
    svc.getDetails({ placeId, fields: ['geometry', 'formatted_address', 'name'] }, (place, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) return;
      const loc = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
      setAddress(place.formatted_address || place.name || '');
      setCenter(loc);
      setLatInput(loc.lat.toFixed(6));
      setLngInput(loc.lng.toFixed(6));
      mapRef.current?.panTo(loc);
    });
  }, [ensurePlacesService]);

  useEffect(() => {
    if (open) {
      const addr = initialData?.address || '';
      const r = initialData?.radius || 200;
      const loc = initialData?.lat && initialData?.lng
        ? { lat: initialData.lat, lng: initialData.lng }
        : DEFAULT_CENTER;
      setAddress(addr);
      setRadius(r);
      setCenter(loc);
      setLatInput(loc.lat.toFixed(6));
      setLngInput(loc.lng.toFixed(6));
    }
  }, [open, initialData]);

  const updateLocation = useCallback((loc: { lat: number; lng: number }, doGeocode = true) => {
    setCenter(loc);
    setLatInput(loc.lat.toFixed(6));
    setLngInput(loc.lng.toFixed(6));
    mapRef.current?.panTo(loc);
    if (doGeocode && mapsAvailable && typeof google !== 'undefined') {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: loc }, (results, status) => {
        if (status === 'OK' && results?.[0]) setAddress(results[0].formatted_address);
      });
    }
  }, [mapsAvailable]);

  const onMapLoad = useCallback((map: google.maps.Map) => { mapRef.current = map; }, []);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) updateLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [updateLocation]);

  const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) updateLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [updateLocation]);

  const handleLatLngBlur = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      updateLocation({ lat, lng });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        <IconButton aria-label={t('common:actions.close')} size="small" onClick={onClose}><CloseOutlined fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left panel — form */}
          <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, position: 'relative', zIndex: 2 }}>
            <Stack spacing={1.5}>
              {extraFields}

              {/* Divider between entity fields and location fields */}
              {extraFields && (
                <Box sx={{ pt: 0.5, pb: 0.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem' }}>
                    {t('attendance:geofences.locationLabel')}
                  </Typography>
                </Box>
              )}

              {/* Address — read-only display, set by map/search */}
              <TextField
                label={t('attendance:geofences.addressLabel')}
                value={address}
                onChange={e => setAddress(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  readOnly: mapsAvailable,
                  startAdornment: (
                    <InputAdornment position="start">
                      <MyLocationOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />

              {/* Lat / Lng editable */}
              <Stack direction="row" spacing={1}>
                <TextField
                  label={t('attendance:geofences.latitudeLabel')}
                  value={latInput}
                  onChange={e => setLatInput(e.target.value)}
                  onBlur={handleLatLngBlur}
                  size="small"
                  fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" color="text.disabled">N</Typography></InputAdornment> }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                />
                <TextField
                  label={t('attendance:geofences.longitudeLabel')}
                  value={lngInput}
                  onChange={e => setLngInput(e.target.value)}
                  onBlur={handleLatLngBlur}
                  size="small"
                  fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" color="text.disabled">E</Typography></InputAdornment> }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                />
              </Stack>

              {/* Radius slider */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{t('attendance:geofences.radiusLabel')}</Typography>
                  <Chip label={`${radius}m`} size="small" sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', fontWeight: 600 }} />
                </Box>
                <Slider
                  value={radius}
                  onChange={(_, v) => setRadius(v as number)}
                  min={50}
                  max={2000}
                  step={10}
                  valueLabelDisplay="auto"
                  valueLabelFormat={v => `${v}m`}
                  size="small"
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.disabled">50m</Typography>
                  <Typography variant="caption" color="text.disabled">2000m</Typography>
                </Box>
              </Box>

              {/* Hint */}
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <GpsFixedOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {t('attendance:geofences.mapHint')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Right panel — map with search overlay */}
          <Box sx={{ flex: 1, minHeight: 420, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', position: 'relative' }}>
            {/* Search bar overlaid on map — MUI Autocomplete driven by
                AutocompleteService (predictions render inside the dialog,
                not Google's pac-container which hides behind the modal). */}
            {mapsAvailable && (
              <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 1 }}>
                <Autocomplete
                  freeSolo
                  filterOptions={(x) => x}
                  options={options}
                  loading={optionsLoading}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.description)}
                  isOptionEqualToValue={(o, v) => o.place_id === v.place_id}
                  onInputChange={(_e, v) => setSearchInput(v)}
                  onChange={(_e, v) => { if (v && typeof v !== 'string') resolvePlace(v.place_id); }}
                  noOptionsText={t('attendance:geofences.searchNoResults', { defaultValue: 'No matching places' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('attendance:geofences.searchPlaceholder')}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {optionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                        sx: {
                          bgcolor: '#ffffff',
                          borderRadius: 1,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          '& fieldset': { border: 'none' },
                          fontSize: '0.85rem',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            )}

            {mapsAvailable ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                zoom={15}
                onLoad={onMapLoad}
                onClick={onMapClick}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <MarkerF
                  position={center}
                  draggable
                  onDragEnd={onMarkerDragEnd}
                />
                <CircleF
                  center={center}
                  radius={radius}
                  options={{
                    fillColor: '#2E65D4',
                    fillOpacity: 0.12,
                    strokeColor: '#2E65D4',
                    strokeOpacity: 0.7,
                    strokeWeight: 2,
                  }}
                />
              </GoogleMap>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: 'grey.100' }}>
                <Typography variant="body2" color="text.secondary">
                  {loadError ? t('common:location.mapUnavailable') : t('attendance:geofences.loadingMap')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pb: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} size="small" sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('common:actions.cancel')}</Button>
        <Button variant="contained" onClick={() => onSave({ address, radius, lat: center.lat, lng: center.lng })} size="small" disabled={saveDisabled || !address.trim()} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{finalSaveLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

const GeofenceMapDialog: React.FC<GeofenceMapDialogProps> = (props) => {
  if (!props.open) return null;
  return (
    <GoogleMapsErrorBoundary
      fallback={(
        <GoogleMapsUnavailableDialog
          open
          title={props.title}
          onClose={props.onClose}
        />
      )}
    >
      <GeofenceMapDialogLoaded {...props} />
    </GoogleMapsErrorBoundary>
  );
};

export default GeofenceMapDialog;
